import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import * as employerApi from '@/services/api/endpoints/employer'
import { EmployerDashboardStats, EmployerProfile, UpdateEmployerProfilePayload } from '@/services/api/endpoints/employer'
import * as jobsApi from '@/services/api/endpoints/jobs'
import { BackendJob, JobPayload, JobStatus, RecommendedCandidate } from '@/services/api/endpoints/jobs'
import * as applicationsApi from '@/services/api/endpoints/applications'
import { BackendApplication, CreateOfferPayload, UpdateApplicationStatusPayload } from '@/services/api/endpoints/applications'
import * as interviewsApi from '@/services/api/endpoints/interviews'
import { Interview, ScheduleInterviewPayload, UpdateInterviewPayload } from '@/services/api/endpoints/interviews'
import * as subscriptionsApi from '@/services/api/endpoints/subscriptions'
import { SubscriptionLimits } from '@/services/api/endpoints/subscriptions'
import { ApiError } from '@/services/api/types'

interface EmployerState {
  candidateRecommendations: RecommendedCandidate[]
  candidateRecommendationsLoading: boolean
  dashboardStats: EmployerDashboardStats | null
  dashboardStatsLoading: boolean
  interviews: Interview[]
  interviewsLoading: boolean
  jobApplications: BackendApplication[]
  jobApplicationsLoading: boolean
  limits: SubscriptionLimits | null
  limitsLoading: boolean
  pipelineApplications: BackendApplication[]
  pipelineLoading: boolean
  postedJobs: BackendJob[]
  postedJobsLoading: boolean
  profile: EmployerProfile | null
  profileLoading: boolean
}

const initialState: EmployerState = {
  candidateRecommendations: [],
  candidateRecommendationsLoading: false,
  dashboardStats: null,
  dashboardStatsLoading: false,
  interviews: [],
  interviewsLoading: false,
  jobApplications: [],
  jobApplicationsLoading: false,
  limits: null,
  limitsLoading: false,
  pipelineApplications: [],
  pipelineLoading: false,
  postedJobs: [],
  postedJobsLoading: false,
  profile: null,
  profileLoading: false,
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof ApiError ? error.message : fallback)

export const fetchEmployerProfile = createAsyncThunk('employer/fetchProfile', async (_, { rejectWithValue }) => {
  try {
    return await employerApi.getEmployerProfile()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load employer profile'))
  }
})

export const updateEmployerProfile = createAsyncThunk(
  'employer/updateProfile',
  async (payload: UpdateEmployerProfilePayload, { rejectWithValue }) => {
    try {
      const profile = await employerApi.updateEmployerProfile(payload)
      toast.success('Profile updated')
      return profile
    } catch (error) {
      const message = errorMessage(error, 'Failed to update profile')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchDashboardStats = createAsyncThunk('employer/fetchDashboardStats', async (_, { rejectWithValue }) => {
  try {
    return await employerApi.getEmployerDashboardStats()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load dashboard stats'))
  }
})

export const fetchPostedJobs = createAsyncThunk('employer/fetchPostedJobs', async (_, { rejectWithValue }) => {
  try {
    const { data } = await jobsApi.listMyPostedJobs()
    return data
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load posted jobs'))
  }
})

export const fetchCandidateRecommendations = createAsyncThunk('employer/fetchCandidateRecommendations', async (_, { rejectWithValue }) => {
  try {
    return await jobsApi.listCandidateRecommendations()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load candidate recommendations'))
  }
})

export const createJobPosting = createAsyncThunk('employer/createJob', async (payload: JobPayload, { rejectWithValue }) => {
  try {
    const job = await jobsApi.createJob(payload)
    toast.success('Job posted')
    return job
  } catch (error) {
    const message = errorMessage(error, 'Failed to create job')
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const updateJobPosting = createAsyncThunk(
  'employer/updateJob',
  async ({ id, payload }: { id: string; payload: Partial<JobPayload> & { status?: JobStatus } }, { rejectWithValue }) => {
    try {
      const job = await jobsApi.updateJob(id, payload)
      toast.success('Job updated')
      return job
    } catch (error) {
      const message = errorMessage(error, 'Failed to update job')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const deleteJobPosting = createAsyncThunk('employer/deleteJob', async (id: string, { rejectWithValue }) => {
  try {
    await jobsApi.deleteJob(id)
    toast.success('Job deleted')
    return id
  } catch (error) {
    const message = errorMessage(error, 'Failed to delete job')
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const fetchApplicationsForJob = createAsyncThunk('employer/fetchApplicationsForJob', async (jobId: string, { rejectWithValue }) => {
  try {
    const { data } = await applicationsApi.listApplicationsForJob(jobId)
    return data
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load applicants'))
  }
})

export const fetchPipelineApplications = createAsyncThunk(
  'employer/fetchPipelineApplications',
  async (params: { limit?: number; page?: number } | undefined, { rejectWithValue }) => {
    try {
      const { data } = await applicationsApi.listApplicationsToMyJobs(params)
      return data
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load applications'))
    }
  },
)

export const updateApplicationStatus = createAsyncThunk(
  'employer/updateApplicationStatus',
  async ({ id, payload }: { id: string; payload: UpdateApplicationStatusPayload }, { rejectWithValue }) => {
    try {
      const application = await applicationsApi.updateApplicationStatus(id, payload)
      toast.success('Application updated')
      return application
    } catch (error) {
      const message = errorMessage(error, 'Failed to update application')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const createOffer = createAsyncThunk(
  'employer/createOffer',
  async ({ applicationId, payload }: { applicationId: string; payload: CreateOfferPayload }, { rejectWithValue }) => {
    try {
      await applicationsApi.createOffer(applicationId, payload)
      toast.success('Offer sent')
      // The application's status flips to OFFER_EXTENDED server-side - refetch it
      // for the up-to-date record rather than guessing the shape locally.
      return await applicationsApi.getApplication(applicationId)
    } catch (error) {
      const message = errorMessage(error, 'Failed to send offer')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchInterviews = createAsyncThunk('employer/fetchInterviews', async (_, { rejectWithValue }) => {
  try {
    return await interviewsApi.listInterviews()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load interviews'))
  }
})

export const scheduleInterview = createAsyncThunk(
  'employer/scheduleInterview',
  async (payload: ScheduleInterviewPayload, { rejectWithValue }) => {
    try {
      const interview = await interviewsApi.scheduleInterview(payload)
      toast.success('Interview scheduled')
      return interview
    } catch (error) {
      const message = errorMessage(error, 'Failed to schedule interview')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const updateInterview = createAsyncThunk(
  'employer/updateInterview',
  async ({ id, payload }: { id: string; payload: UpdateInterviewPayload }, { rejectWithValue }) => {
    try {
      const interview = await interviewsApi.updateInterview(id, payload)
      toast.success('Interview updated')
      return interview
    } catch (error) {
      const message = errorMessage(error, 'Failed to update interview')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchEmployerLimits = createAsyncThunk('employer/fetchLimits', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getSubscriptionLimits()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load subscription limits'))
  }
})

const employerSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployerProfile.pending, (state) => {
        state.profileLoading = true
      })
      .addCase(fetchEmployerProfile.fulfilled, (state, action) => {
        state.profile = action.payload
        state.profileLoading = false
      })
      .addCase(fetchEmployerProfile.rejected, (state, action) => {
        state.profileLoading = false
        toast.error(action.payload as string)
      })
      .addCase(updateEmployerProfile.fulfilled, (state, action) => {
        state.profile = action.payload
      })
      .addCase(fetchDashboardStats.pending, (state) => {
        state.dashboardStatsLoading = true
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardStats = action.payload
        state.dashboardStatsLoading = false
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.dashboardStatsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchPostedJobs.pending, (state) => {
        state.postedJobsLoading = true
      })
      .addCase(fetchPostedJobs.fulfilled, (state, action) => {
        state.postedJobs = action.payload
        state.postedJobsLoading = false
      })
      .addCase(fetchPostedJobs.rejected, (state, action) => {
        state.postedJobsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchCandidateRecommendations.pending, (state) => {
        state.candidateRecommendationsLoading = true
      })
      .addCase(fetchCandidateRecommendations.fulfilled, (state, action) => {
        state.candidateRecommendations = action.payload
        state.candidateRecommendationsLoading = false
      })
      .addCase(fetchCandidateRecommendations.rejected, (state, action) => {
        state.candidateRecommendationsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(createJobPosting.fulfilled, (state, action) => {
        state.postedJobs.unshift(action.payload)
      })
      .addCase(updateJobPosting.fulfilled, (state, action) => {
        const index = state.postedJobs.findIndex((job) => job.id === action.payload.id)
        if (index !== -1) state.postedJobs[index] = action.payload
      })
      .addCase(deleteJobPosting.fulfilled, (state, action) => {
        state.postedJobs = state.postedJobs.filter((job) => job.id !== action.payload)
      })
      .addCase(fetchApplicationsForJob.pending, (state) => {
        state.jobApplicationsLoading = true
      })
      .addCase(fetchApplicationsForJob.fulfilled, (state, action) => {
        state.jobApplications = action.payload
        state.jobApplicationsLoading = false
      })
      .addCase(fetchApplicationsForJob.rejected, (state, action) => {
        state.jobApplicationsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchPipelineApplications.pending, (state) => {
        state.pipelineLoading = true
      })
      .addCase(fetchPipelineApplications.fulfilled, (state, action) => {
        state.pipelineApplications = action.payload
        state.pipelineLoading = false
      })
      .addCase(fetchPipelineApplications.rejected, (state, action) => {
        state.pipelineLoading = false
        toast.error(action.payload as string)
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        const lists = [state.pipelineApplications, state.jobApplications]
        lists.forEach((list) => {
          const index = list.findIndex((application) => application.id === action.payload.id)
          if (index !== -1) list[index] = action.payload
        })
      })
      .addCase(createOffer.fulfilled, (state, action) => {
        const lists = [state.pipelineApplications, state.jobApplications]
        lists.forEach((list) => {
          const index = list.findIndex((application) => application.id === action.payload.id)
          if (index !== -1) list[index] = action.payload
        })
      })
      .addCase(fetchInterviews.pending, (state) => {
        state.interviewsLoading = true
      })
      .addCase(fetchInterviews.fulfilled, (state, action) => {
        state.interviews = action.payload
        state.interviewsLoading = false
      })
      .addCase(fetchInterviews.rejected, (state, action) => {
        state.interviewsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(scheduleInterview.fulfilled, (state, action) => {
        state.interviews.unshift(action.payload)
      })
      .addCase(updateInterview.fulfilled, (state, action) => {
        const index = state.interviews.findIndex((interview) => interview.id === action.payload.id)
        if (index !== -1) state.interviews[index] = action.payload
      })
      .addCase(fetchEmployerLimits.pending, (state) => {
        state.limitsLoading = true
      })
      .addCase(fetchEmployerLimits.fulfilled, (state, action) => {
        state.limits = action.payload
        state.limitsLoading = false
      })
      .addCase(fetchEmployerLimits.rejected, (state, action) => {
        state.limitsLoading = false
        toast.error(action.payload as string)
      })
  },
  initialState,
  name: 'employer',
  reducers: {},
})

interface RootStateSlice {
  employer: EmployerState
}

export const selectEmployerProfile = (state: RootStateSlice): EmployerProfile | null => state.employer.profile
export const selectEmployerProfileLoading = (state: RootStateSlice): boolean => state.employer.profileLoading
export const selectIsEmployerVerified = (state: RootStateSlice): boolean => state.employer.profile?.verificationStatus === 'VERIFIED'
export const selectDashboardStats = (state: RootStateSlice): EmployerDashboardStats | null => state.employer.dashboardStats
export const selectDashboardStatsLoading = (state: RootStateSlice): boolean => state.employer.dashboardStatsLoading
export const selectPostedJobs = (state: RootStateSlice): BackendJob[] => state.employer.postedJobs
export const selectPostedJobsLoading = (state: RootStateSlice): boolean => state.employer.postedJobsLoading
export const selectCandidateRecommendations = (state: RootStateSlice): RecommendedCandidate[] => state.employer.candidateRecommendations
export const selectCandidateRecommendationsLoading = (state: RootStateSlice): boolean => state.employer.candidateRecommendationsLoading
export const selectJobApplications = (state: RootStateSlice): BackendApplication[] => state.employer.jobApplications
export const selectJobApplicationsLoading = (state: RootStateSlice): boolean => state.employer.jobApplicationsLoading
export const selectPipelineApplications = (state: RootStateSlice): BackendApplication[] => state.employer.pipelineApplications
export const selectPipelineLoading = (state: RootStateSlice): boolean => state.employer.pipelineLoading
export const selectInterviews = (state: RootStateSlice): Interview[] => state.employer.interviews
export const selectInterviewsLoading = (state: RootStateSlice): boolean => state.employer.interviewsLoading
export const selectEmployerLimits = (state: RootStateSlice): SubscriptionLimits | null => state.employer.limits
export const selectCanPostJob = (state: RootStateSlice): boolean => {
  const jobPostings = state.employer.limits?.limits.jobPostings
  if (!jobPostings) return true
  return jobPostings.limit === null || (jobPostings.remaining ?? 0) > 0
}

export default employerSlice.reducer
